import axios, { AxiosResponse } from 'axios';
import { API_MAPPING } from './api-url-map';

export type RequestOptions = {
    headers?: Record<string, string>;
    body?: Record<string, any>;
    requestType?: 'get' | 'GET' | 'post' | 'POST';
    pathExtension?: string;
    query?: Record<string, string>;
}

type AxiosOptions = {
    headers?: Record<string, string>;
    data?: Record<string, any>;
    method?: 'get' | 'GET' | 'post' | 'POST';
}

export interface DynamicResponse<T> {
    status: number;
    data: T;
}

const mockApiResponsesEnabled = process.env.REACT_APP_NODE_ENV === 'development'
    || process.env.NODE_ENV !== 'production';

/**
 * Dynamically performs a request to a production server or returns mock data depending on
 * if this application is running on an author instance.
 *
 * Result will be returned in the form of a promise, mapped to the provided type.
 *
 * @param {string} name Name of the endpoint, see {@code apiUrlMap}
 * @param {RequestOptions} opts Axios Request Options
 */
export async function dynamicRequest<Type>(name: keyof typeof API_MAPPING, opts?: RequestOptions, params?: string): Promise<DynamicResponse<Type>> {
    const isAuthor = mockApiResponsesEnabled
        && ((window.location.host.indexOf('4502') > -1) || window.location.host.includes('localhost'));
    const options: AxiosOptions = {};
    const requestType = opts?.requestType || 'get';

    let url = API_MAPPING[name].endpoint.toLowerCase();

    if (opts?.headers) {
        options.headers = opts.headers;
    }

    if (opts?.body) {
        options.data = opts.body;
    }

    if (opts?.pathExtension) {
        url += opts.pathExtension;
    }

    if (opts?.query) {
        url += parseQueryAsString(opts.query);
    }

    if (params) {
        url += params;
    }

    return new Promise<DynamicResponse<Type>>(async (resolve, reject) => {
        if (isAuthor) {
            const obj = API_MAPPING[name].mock as any;

            const result: DynamicResponse<Type> = {
                status: obj.status,
                data: obj.data,
            };
            return resolve(result);
        }

        try {
            
            const response: AxiosResponse<Type, any> = await axios.request<Type>({ method: requestType, url: url, ...options });

            const result: DynamicResponse<Type> = {
                status: response.status,
                data: response.data,
            };

            return resolve(result);
        } catch (err) {
            reject(err);
        }
    });
}

function parseQueryAsString(queryObj: Record<string, string>): string {
    let query = '';

    Object.keys(queryObj).forEach((param, i) => {
        const paramValue = encodeURI(queryObj[param]);
        query += `${(i === 0) ? '?' : '&'}${param}=${paramValue}`;
    });

    return query;
}
